import { owlWindowBounds } from "../../../supabase/functions/_shared/owl-window.ts";
import { useSearchParams } from "react-router-dom";
import { OwlWorkspaceNav, OwlDirectory, type OwlWorkspaceView } from "./OwlWorkspace.tsx";
import "./owl-workspace.css";
// Owl Hour Accumulator — main page.
// Faithfully matches the prototype layout:
//   1. owl-head — page title + action buttons
//   2. owl-hero — 4-col session window banner
//   3. owl-tiers-tabs — tier picker
//   4. owl-slip — tonight's slip for selected tier
//   5. owl-grid — "How it works" flow + region coverage
//   6. owl-history — recent results
//   7. owl-morning-reveal — result screen (when results exist)
//   8. Subscription gate — shown when not subscribed

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLeagues } from "../../leagues/useLeagues.ts";
import { useOwlHourSlips, OWL_TIER_META, type OwlTier } from "../../owl/useOwlHourSlips.ts";
import { useOwlHourUserSlips } from "../../owl/useOwlHourUserSlips.ts";
import { useOwlSubscription } from "../../owl/useOwlSubscription.ts";
import {
  isLockCtaActive,
  owlActiveRefreshPhase,
  owlSessionDate,
  resolveOwlScreenState,
} from "../../owl/owlScreenState.ts";
import { parseContinentFlags } from "../../owl/owlContinentFlags.ts";
import { OwlHero } from "./OwlHero.tsx";
import { OwlTierSelector } from "./OwlTierSelector.tsx";
import { OwlUnpricedPanel } from "./OwlUnpricedPanel.tsx";
import { useOwlUnpricedPicks } from "../../owl/useOwlUnpricedPicks.ts";
import { OwlSignalCard } from "./OwlSignalCard.tsx";
import { OwlSpotlight } from "./OwlSpotlight.tsx";
import { OwlFlowSection } from "./OwlFlowSection.tsx";
import { OwlHistory } from "./OwlHistory.tsx";
import { OwlSystemTrackRecord } from "./OwlSystemTrackRecord.tsx";
import { OwlPipelineStatusStrip } from "./OwlPipelineStatusStrip.tsx";
import { OwlOperationsPanel } from "./OwlOperationsPanel.tsx";

import { OwlWatchList } from "./OwlWatchList.tsx";

import { OwlMorningReveal } from "./OwlMorningReveal.tsx";
import { OwlStakeDrawer } from "./OwlStakeDrawer.tsx";
import { DoctrineSection } from "../../components/DoctrineSection.tsx";
import { useDoctrinePicks } from "../../doctrine/useDoctrinePicks.ts";

// ─── Subscription gate ────────────────────────────────────────

function OwlSubscriptionGate(): JSX.Element {
  const { price, startTrial, loading } = useOwlSubscription();
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    setStarting(true);
    await startTrial();
    setStarting(false);
  };

  return (
    <div className="owl-page owl-workspace">
      <div className="owl-head">
        <div>
          <h1>
            <span className="moon">🦉</span>
            OWL HOUR
          </h1>
          <div className="sub">Overnight football selections and recorded results.</div>
        </div>
      </div>
      <div
        style={{
          background: "var(--owl-elev)",
          border: "1px solid var(--owl-border)",
          borderRadius: 16,
          padding: "64px 32px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          maxWidth: 520,
          margin: "40px auto",
        }}
      >
        <span style={{ fontSize: 56, lineHeight: 1 }}>🦉</span>
        <span
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          Owl Hour Accumulator
        </span>
        <span
          style={{
            fontSize: 13.5,
            color: "var(--owl-text-2)",
            lineHeight: 1.6,
            maxWidth: 400,
          }}
        >
          Overnight football intelligence, five priced boards and a separate unpriced feed. Review
          the selections and follow verified results.
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 28,
            fontWeight: 700,
            color: "var(--owl-violet)",
          }}
        >
          {price.currency}
          {price.standalone}
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--owl-text-3)",
            }}
          >
            /month
          </span>
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--owl-text-2)",
          }}
        >
          or {price.currency}
          {price.addon}/month with any paid STRIKZA tier
        </span>
        <button
          type="button"
          className="btn primary"
          style={{ marginTop: 8 }}
          onClick={handleStart}
          disabled={starting || loading}
        >
          {starting ? "Starting…" : "Start 7-Day Free Trial"}
        </button>
        <span style={{ fontSize: 12, color: "var(--owl-text-3)" }}>
          Full access to all five tiers for 7 days. No charge until trial ends.
        </span>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────

export function OwlPage(): JSX.Element {
  const { leagues } = useLeagues({ statusFilter: "active" });
  const { slips, loading, error: publicationError, refetch: retryPublications } = useOwlHourSlips();
  const { userSlips } = useOwlHourUserSlips();
  const { subscribed, trial, trialDaysLeft, loading: accessLoading } = useOwlSubscription();
  const [search, setSearch] = useSearchParams();
  const view: OwlWorkspaceView = ["boards", "history", "about"].includes(search.get("view") ?? "")
    ? (search.get("view") as OwlWorkspaceView)
    : "overview";
  const setView = (next: OwlWorkspaceView) =>
    setSearch((prev) => {
      const params = new URLSearchParams(prev);
      if (next === "overview") params.delete("view");
      else params.set("view", next);
      return params;
    });
  const [selectedTier, setSelectedTier] = useState<OwlTier>("moderate");
  const [unpricedSelected, setUnpricedSelected] = useState(false);
  const unpriced = useOwlUnpricedPicks();
  const [stakeOpen, setStakeOpen] = useState(false);
  const [lockedBySlip, setLockedBySlip] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem("owl_locked_stakes_by_slip");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const lockedStakes = useMemo<Partial<Record<OwlTier, number>>>(() => {
    const current: Partial<Record<OwlTier, number>> = {};
    for (const slip of slips) {
      if (lockedBySlip[slip.id] !== undefined) current[slip.tier] = lockedBySlip[slip.id];
    }
    return current;
  }, [slips, lockedBySlip]);
  const flowRef = useRef<HTMLDivElement>(null);

  // Doctrine fallback — load probability + value picks when no T1 slip exists.
  const t1FixtureIds = useMemo(
    () => slips.flatMap((s) => s.legs.map((l) => l.fixture_id)),
    [slips],
  );
  const sessionDate = owlSessionDate();
  const sessionEnd = new Date(`${sessionDate}T00:00:00Z`);
  sessionEnd.setUTCDate(sessionEnd.getUTCDate() + 1);
  const {
    probabilityPicks: doctrineProbPicks,
    valuePicks: doctrineValuePicks,
    substrateEmpty,
    substrateWeek,
  } = useDoctrinePicks({
    t1HasPicks: slips.some((s) => s.legs.length > 0),
    excludeFixtureIds: t1FixtureIds,
    maxPicks: 5,
    substrateView: "vw_owl_substrate_fixtures",
    fixtureWindow: owlWindowBounds(sessionDate,owlActiveRefreshPhase()??"noon"),
  });

  // GAP 1/2 — compute screen state + continent flags up-front so hooks are
  // unconditional and survive the subscription-gate / loading early returns.
  const selectedSlipEarly = slips.find((sl) => sl.tier === selectedTier);
  const isLockedEarly =
    lockedStakes[selectedTier] !== undefined && lockedStakes[selectedTier] !== null;
  const screenState = resolveOwlScreenState(
    new Date(),
    selectedSlipEarly !== undefined,
    isLockedEarly,
  );
  const ctaActive =
    !unpricedSelected &&
    (isLockCtaActive(screenState) ||
      (isLockedEarly && selectedSlipEarly?.status === "published")) &&
    !!selectedSlipEarly &&
    selectedSlipEarly.legs.length > 0 &&
    Number.isFinite(selectedSlipEarly.combined_odds) &&
    Number(selectedSlipEarly.combined_odds) > 1 &&
    selectedSlipEarly.legs.every((l) => Number.isFinite(l.odds) && l.odds > 1);
  const continentFlags = useMemo(
    () => parseContinentFlags(selectedSlipEarly?.continent_flags ?? {}),
    [selectedSlipEarly],
  );

  // Persist locked stakes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("owl_locked_stakes_by_slip", JSON.stringify(lockedBySlip));
    } catch {
      /* quota exceeded — ignore */
    }
  }, [lockedBySlip]);

  const scrollToFlow = useCallback(() => {
    setSearch((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", "about");
      return next;
    });
    flowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [setSearch]);

  const handleLockStake = useCallback(
    (amount: number) => {
      if (selectedSlipEarly) {
        setLockedBySlip((prev) => ({ ...prev, [selectedSlipEarly.id]: amount }));
      }
    },
    [selectedSlipEarly],
  );

  // Subscription gate
  if (accessLoading)
    return (
      <div className="owl-page owl-workspace">
        <p role="status">Loading OWL HOUR…</p>
      </div>
    );
  if (!subscribed) {
    return <OwlSubscriptionGate />;
  }

  const cur = OWL_TIER_META[selectedTier];
  const selectedSlip = selectedSlipEarly;

  // Find most recent user slip with a result for morning reveal
  const latestResult = userSlips.find((us) => us.result !== null);
  const latestResultSlip =
    latestResult !== undefined ? slips.find((sl) => sl.id === latestResult.owl_slip_id) : undefined;

  const openBoard = (tier: OwlTier | "unpriced") => {
    setUnpricedSelected(tier === "unpriced");
    if (tier !== "unpriced") setSelectedTier(tier);
    setView("boards");
  };
  return (
    <div className="owl-page owl-workspace">
      <OwlWorkspaceNav view={view} onChange={setView} />
      <header className="owl-head">
        <div>
          <span className="owl-eyebrow">OVERNIGHT FOOTBALL INTELLIGENCE</span>
          <h1>OWL HOUR</h1>
          <p className="sub">
            Published boards, exact selections and a clear record of every night.
          </p>
        </div>
        <div className="actions">
          <span className="owl-session-date">
            {new Date(`${sessionDate}T12:00:00Z`).toLocaleDateString("en-GB", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: "Africa/Accra",
            })}{" "}
            · Accra
          </span>
          <button type="button" className="btn" onClick={scrollToFlow}>
            How it works ↗
          </button>
        </div>
      </header>
      {trial && trialDaysLeft > 0 ? (
        <div className="owl-trial-note">
          <span>
            Trial access · {trialDaysLeft} {trialDaysLeft === 1 ? "day" : "days"} remaining
          </span>
          <a href="/pricing">Subscription options ↗</a>
        </div>
      ) : null}
      <OwlPipelineStatusStrip slips={slips} loading={loading} error={publicationError} />
      {publicationError ? (
        <div className="owl-error" role="alert">
          Couldn't load OWL HOUR.{" "}
          <button type="button" onClick={retryPublications}>
            Retry
          </button>
        </div>
      ) : null}
      {view === "overview" ? (
        <>
          <OwlDirectory
            slips={slips}
            unpricedCount={unpriced.picks.length}
            loading={loading}
            error={publicationError}
            onOpen={openBoard}
          />
          <OwlSpotlight sessionDate={sessionDate} />
          <OwlOperationsPanel />
        </>
      ) : null}
      {view === "boards" ? (
        <>
          <OwlTierSelector
            selectedTier={selectedTier}
            onSelect={(tier) => {
              setUnpricedSelected(false);
              setSelectedTier(tier);
            }}
            unpricedSelected={unpricedSelected}
            onSelectUnpriced={() => {
              setStakeOpen(false);
              setUnpricedSelected(true);
            }}
            unpricedCount={unpriced.picks.length}
            slips={slips}
            userSlips={userSlips}
            lockedStakes={lockedStakes}
          />
          {!unpricedSelected ? (
            <OwlHero leagues={leagues} slips={slips} selectedTier={selectedTier} />
          ) : null}
          {unpricedSelected ? (
            <OwlUnpricedPanel {...unpriced} onRetry={unpriced.refetch} />
          ) : (
            <OwlSignalCard
              slip={selectedSlip}
              tierKey={selectedTier}
              lockedStake={lockedStakes[selectedTier] ?? null}
              onLockClick={() => setStakeOpen(true)}
              screenState={screenState}
              allSlips={slips}
              onTierDegrade={setSelectedTier}
              stakeAction={
                <button
                  type="button"
                  className="btn primary"
                  disabled={!ctaActive}
                  data-testid="owl-lock-cta"
                  data-cta-active={ctaActive ? "true" : "false"}
                  onClick={() => setStakeOpen(true)}
                >
                  {isLockedEarly ? "Change stake" : `Review ${cur.name} stake`}
                </button>
              }
            />
          )}
          <details className="owl-secondary">
            <summary>Fixture watch & additional predictions</summary>
            <OwlWatchList sessionDate={sessionDate} />
            <DoctrineSection
              probabilityPicks={doctrineProbPicks}
              valuePicks={doctrineValuePicks}
              isFullFallback={!slips.some((s) => s.legs.length > 0)}
              substrateEmpty={substrateEmpty}
              substrateWeek={substrateWeek}
            />
          </details>
        </>
      ) : null}
      {view === "history" ? (
        <>
          <div className="owl-section-heading">
            <span className="owl-eyebrow">THE PUBLICATION RECORD</span>
            <h2>History & settlement</h2>
            <p>Your saved nights and the system's recorded publications.</p>
          </div>
          {latestResult !== undefined && latestResultSlip !== undefined ? (
            <OwlMorningReveal userSlip={latestResult} slip={latestResultSlip} />
          ) : null}
          <OwlHistory userSlips={userSlips} />
          <OwlSystemTrackRecord />
        </>
      ) : null}
      {view === "about" ? (
        <div ref={flowRef}>
          <OwlFlowSection leagues={leagues} continentFlags={continentFlags} />
        </div>
      ) : null}
      <footer className="owl-workspace-footer">
        <span>Intelligence, not certainty. Every selection carries risk.</span>
        <span>All times shown in Accra · UTC</span>
      </footer>
      <OwlStakeDrawer
        tier={selectedTier}
        slip={selectedSlip}
        open={stakeOpen}
        onClose={() => setStakeOpen(false)}
        onLock={handleLockStake}
      />
    </div>
  );
}
